import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeForm,
  TeIcon,
  TeInputText,
  TeLabel,
  TeModal,
  TeText,
} from '@topcon/tuicl-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resetFormWithName, submitFormWithName } from '@topcon/tuicl';
import { useNotification } from '@topcon/topcon-utils';

import { zChangeOwnershipRequestPostBody } from '../../api/generated/pm/zod.gen';
import IconWrapper from '../../components/IconWrapper';
import { background } from '../../providers/theme/theme.index';
import { useRequestChangeOwnership } from '../RequestChangeOwnership/changeOwnership.mutations';

interface Props {
  open: boolean;
  onClose: () => void;
}

const FORM_NAME = 'transferOwnerForm';

const TransferOwnerModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation('settings');

  const notificationService = useNotification();
  const [ownerEmail, setOwnerEmail] = useState('');
  const { mutateAsync: requestChangeOwnership, isPending: isPendingRequestChangeOwnership } =
    useRequestChangeOwnership();

  const emailValidation = zChangeOwnershipRequestPostBody.safeParse({
    requestedOwnerEmail: ownerEmail,
  });
  const isValidEmail = ownerEmail.length > 0 && emailValidation.success;

  const onCancelClick = () => {
    resetFormWithName(FORM_NAME);
    setOwnerEmail('');
    onClose();
  };

  const handleFormSubmit = async () => {
    if (!isValidEmail) {
      notificationService.addNotification(
        t('error', { ns: 'common' }),
        t('formValidationError', { ns: 'common' }),
        'error'
      );
      return;
    }

    const normalizedEmail = ownerEmail.toLowerCase().trim();

    await requestChangeOwnership(
      { requestedOwnerEmail: normalizedEmail },
      {
        onSuccess: () => {
          onClose();
        },
        onError: () => {
          notificationService.addNotification(
            t('generalInformation.transferOwner.modal.ownerTransferFailureTitle'),
            t('generalInformation.transferOwner.modal.ownerTransferFailureDescription'),
            'error'
          );
        },
      }
    );
  };

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={onClose}
      contentPadding={0}
      size="s"
    >
      <TeContainer
        width={480}
        backgroundColor="background.primary"
        padding={[0, 's']}
        column
        grow
        gap={0}
      >
        <TeContainerSlim gap="m" fill column padding={['xl', 0]} alignItems="center">
          <TeContainerSlim column alignItems="center">
            <IconWrapper size={72} noOutline backgroundColor={background.secondary}>
              <TeIcon name="key" color="icon.brand.primary" size="xl" />
            </IconWrapper>
          </TeContainerSlim>
          <TeContainerSlim column gap="xs" alignItems="center">
            <TeText alignment="center" size="xl2" weight="bold" color="text.primary">
              {t('generalInformation.transferOwner.modal.title')}
            </TeText>
            <TeText alignment="center" size="m" weight="regular" color="text.secondary">
              {t('generalInformation.transferOwner.modal.description')}
            </TeText>
          </TeContainerSlim>
        </TeContainerSlim>
        <TeDivider dividerColor="border.secondary" />
        <TeContainerSlim column padding={['l', 0]} grow gap="xs">
          <TeForm name={FORM_NAME} tesubmit={handleFormSubmit}>
            <TeLabel text={t('generalInformation.transferOwner.modal.ownerEmail')} />
            <TeInputText
              readonly={isPendingRequestChangeOwnership}
              valid={isValidEmail}
              size="s"
              name="ownerEmail"
              grow
              placeholder={t('generalInformation.transferOwner.modal.ownerEmailPlaceholder')}
              value={ownerEmail}
              required
              techange={(e) => setOwnerEmail(e.target.value)}
              statusText={
                ownerEmail.length > 1 && !emailValidation.success
                  ? t('generalInformation.transferOwner.modal.invalidEmailError')
                  : ''
              }
            />
          </TeForm>
        </TeContainerSlim>
      </TeContainer>
      <TeButton
        slot="tertiary"
        text={t('cancel')}
        variant="neutral"
        teclick={() => onCancelClick()}
        size="s"
      />
      <TeButton
        teclick={() => submitFormWithName(FORM_NAME)}
        slot="primary"
        text={t('generalInformation.transferOwner.transferButton')}
        variant="primary"
        size="s"
        disabled={isPendingRequestChangeOwnership || !isValidEmail}
        loading={isPendingRequestChangeOwnership}
      />
    </TeModal>
  );
};

export default TransferOwnerModal;
